import { useEffect, useState } from "react";
import { FileText, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, assetSrc } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { STATUS_LABEL, STATUS_STYLES, DOC_TYPE_LABEL, fileExtension } from "../../lib/constants";

const QUICK_TYPES = ["manual", "esquema", "ficha"];
const inputCls =
  "w-full h-11 px-4 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:border-zinc-400 focus:ring-4 focus:ring-zinc-900/5 transition-all";

function InfoRow({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.15em] font-medium text-zinc-400">{label}</p>
      <p className="text-base text-zinc-900 mt-1">{value || "—"}</p>
    </div>
  );
}

// Fechas de calendario locales (sin timezone): "YYYY-MM-DD" → Date en medianoche local.
function parseLocalDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

function warrantyActive(until) {
  const d = parseLocalDate(until);
  if (!d) return null;
  const n = new Date();
  return d >= new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

function fmtWarranty(s) {
  const d = parseLocalDate(s);
  return d ? d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) : s;
}

export default function SummaryTab({ machine }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState([]);
  const [items, setItems] = useState(machine.tech_data || []);
  const [names, setNames] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState("");
  const [formValue, setFormValue] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const status = STATUS_STYLES[machine.status] || STATUS_STYLES.operativa;

  useEffect(() => {
    api.get(`/machines/${machine.id}/documents`).then(({ data }) => setDocs(data)).catch(() => {});
  }, [machine.id]);

  useEffect(() => {
    setItems(machine.tech_data || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machine.id]);

  useEffect(() => {
    if (isAdmin) api.get("/machines/tech-data-names").then(({ data }) => setNames(data)).catch(() => {});
  }, [isAdmin, machine.id]);

  const quickDocs = docs.filter((d) => QUICK_TYPES.includes(d.doc_type)).slice(0, 4);

  const openAdd = () => {
    setEditing(null);
    setFormName("");
    setFormValue("");
    setError("");
    setDlg(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setFormName(d.name);
    setFormValue(d.value);
    setError("");
    setDlg(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editing) {
        const { data } = await api.patch(`/machines/${machine.id}/tech-data/${editing.id}`, { name: formName, value: formValue });
        setItems((arr) => arr.map((d) => (d.id === editing.id ? { ...d, name: data.name, value: data.value } : d)));
        toast.success("Dato actualizado");
      } else {
        const { data } = await api.post(`/machines/${machine.id}/tech-data`, { name: formName, value: formValue });
        setItems((arr) => [...arr, data]);
        setNames((n) => (n.some((x) => x.toLowerCase() === data.name.toLowerCase()) ? n : [...n, data.name]));
        toast.success("Dato añadido");
      }
      setDlg(false);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo guardar el dato");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`¿Eliminar "${d.name}" de esta máquina?`)) return;
    try {
      await api.delete(`/machines/${machine.id}/tech-data/${d.id}`);
      setItems((arr) => arr.filter((x) => x.id !== d.id));
      toast.success("Dato eliminado");
    } catch {
      toast.error("No se pudo eliminar el dato");
    }
  };

  return (
    <div className="space-y-10" data-testid="summary-tab">
      <section className="bg-white rounded-2xl border border-zinc-200/80 card-shadow p-6 sm:p-8">
        <h2 className="font-heading text-xl font-semibold tracking-tight mb-6">Información</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-6">
          <InfoRow label="Fabricante" value={machine.manufacturer} />
          <InfoRow label="Modelo" value={machine.model} />
          <InfoRow label="Número de serie" value={machine.serial_number} />
          {machine.supplier && <InfoRow label="Proveedor" value={machine.supplier} testId="summary-supplier" />}
          {machine.service_application && <InfoRow label="Servicio / aplicación" value={machine.service_application} testId="summary-service-application" />}
          {machine.warranty_until && parseLocalDate(machine.warranty_until) && (
            <div data-testid="summary-warranty">
              <p className="text-xs uppercase tracking-[0.15em] font-medium text-zinc-400">Garantía hasta</p>
              <p className="text-base text-zinc-900 mt-1 flex items-center gap-2 flex-wrap">
                {fmtWarranty(machine.warranty_until)}
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                    warrantyActive(machine.warranty_until) ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                  }`}
                  data-testid="warranty-badge"
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${warrantyActive(machine.warranty_until) ? "bg-emerald-500" : "bg-rose-500"}`} />
                  {warrantyActive(machine.warranty_until) ? "En garantía" : "Fuera de garantía"}
                </span>
              </p>
            </div>
          )}
          <InfoRow label="Año" value={machine.year} />
          <div>
            <p className="text-xs uppercase tracking-[0.15em] font-medium text-zinc-400">Ubicación</p>
            <p className="text-base text-zinc-900 mt-1 flex items-center gap-1.5">
              {machine.location_name ? (<><MapPin size={14} className="text-zinc-400" />{machine.location_name}</>) : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.15em] font-medium text-zinc-400">Estado</p>
            <span className={`inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 rounded-full text-xs font-medium ${status.badge}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
              {STATUS_LABEL[machine.status] || machine.status}
            </span>
          </div>
        </div>
        {machine.description && (
          <div className="mt-7 pt-6 border-t border-zinc-100">
            <p className="text-xs uppercase tracking-[0.15em] font-medium text-zinc-400 mb-2">Descripción</p>
            <p className="text-base text-zinc-600 leading-relaxed whitespace-pre-wrap" data-testid="summary-description">
              {machine.description}
            </p>
          </div>
        )}
      </section>

      {(items.length > 0 || isAdmin) && (
        <section className="bg-white rounded-2xl border border-zinc-200/80 card-shadow p-6 sm:p-8" data-testid="tech-data-section">
          <div className="flex items-center justify-between gap-4 mb-2">
            <h2 className="font-heading text-xl font-semibold tracking-tight">Datos técnicos</h2>
            {isAdmin && (
              <button
                onClick={openAdd}
                data-testid="tech-data-add-button"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors duration-200"
              >
                <Plus size={15} /> Añadir dato
              </button>
            )}
          </div>
          {items.length > 0 && (
            <ul className="divide-y divide-zinc-100">
              {items.map((d) => (
                <li key={d.id} className="group flex items-baseline gap-3 py-3" data-testid="tech-data-row">
                  <span className="text-sm text-zinc-500 min-w-0 break-words sm:shrink-0">{d.name}</span>
                  <span className="flex-1 text-sm font-medium text-zinc-900 text-right break-words">{d.value}</span>
                  {isAdmin && (
                    <span className="flex items-center gap-0.5 shrink-0 sm:opacity-0 sm:group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => openEdit(d)}
                        aria-label={`Editar ${d.name}`}
                        data-testid={`tech-data-edit-${d.id}`}
                        className="p-1.5 text-zinc-300 hover:text-zinc-600 transition-colors duration-200"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={() => remove(d)}
                        aria-label={`Eliminar ${d.name}`}
                        data-testid={`tech-data-delete-${d.id}`}
                        className="p-1.5 text-zinc-300 hover:text-rose-500 transition-colors duration-200"
                      >
                        <Trash2 size={13} />
                      </button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {quickDocs.length > 0 && (
        <section data-testid="quick-docs">
          <h2 className="font-heading text-xl font-semibold tracking-tight mb-4">Acceso rápido</h2>
          <div className="space-y-2.5">
            {quickDocs.map((d) => (
              <button
                key={d.id}
                onClick={() => window.open(assetSrc(d), "_blank")}
                data-testid={`quick-doc-${d.id}`}
                className="w-full flex items-center gap-4 bg-white rounded-2xl border border-zinc-200/80 card-shadow p-4 text-left hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200 active:scale-[0.99]"
              >
                <span className="h-11 w-11 rounded-xl bg-zinc-100 flex items-center justify-center text-zinc-500 shrink-0">
                  <FileText size={18} />
                </span>
                <span className="min-w-0">
                  <span className="block font-medium truncate">{d.name}</span>
                  <span className="block text-sm text-zinc-400">
                    {DOC_TYPE_LABEL[d.doc_type]} · {fileExtension(d.original_filename) || "PDF"}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar dato técnico" : "Añadir dato técnico"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 pt-2">
            <input
              className={inputCls}
              list="tech-data-name-options"
              placeholder="Nombre del dato"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              maxLength={80}
              required
              autoComplete="off"
              data-testid="tech-data-name-input"
            />
            <datalist id="tech-data-name-options">
              {names.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
            <input
              className={inputCls}
              placeholder="Valor"
              value={formValue}
              onChange={(e) => setFormValue(e.target.value)}
              maxLength={160}
              required
              data-testid="tech-data-value-input"
            />
            {error && <p className="text-sm text-rose-600" data-testid="tech-data-error">{error}</p>}
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setDlg(false)}
                className="flex-1 h-11 rounded-full border border-zinc-200 text-sm font-medium text-zinc-600 hover:bg-zinc-50 transition-colors duration-200"
                data-testid="tech-data-cancel"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 h-11 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-700 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50"
                data-testid="tech-data-save"
              >
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
